import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  PostAttachmentList,
  richHtmlSx,
  tiptapDocToHtml,
  useConfirmService,
  useMentionResolver,
  type TiptapDoc,
} from '@onesource/components';
import {
  POST_TYPE,
  POST_TYPE_DEFINITIONS,
  PostStatus,
  type TCommentWithAuthor,
  type TPostWithAuthor,
} from '@onesource/schemas';
import {
  fetchMentionUsersByIds,
  getMediaUrl,
  getSession,
  useDeletePost,
  useGetAllProfileQuery,
  useGetComments,
  useGetSpace,
  useToggleSavePost,
  useUpdateAdminPost,
} from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import DOMPurify from 'dompurify';
import { enqueueSnackbar } from 'notistack';
import { memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import {
  RiBookmarkFill,
  RiBookmarkLine,
  RiErrorWarningLine,
  RiLinkM,
  RiPushpinFill,
  RiPushpinLine,
} from 'react-icons/ri';
import { userCanManageSpace } from 'src/utils/app-access';

import { CommentInput } from './comment-input';
import { CommentItem } from './comment-item';
import { PollCard } from './poll-card';
import { PostActionsMenu, type PostActionItem } from './post-actions-menu';
import { PostComposer } from './post-composer';
import { POST_TYPE_CHIP_COLORS, PostTypeIcon } from './post-type-select';
import { ReactionPicker } from './reaction-picker';
import { ReactorsAvatarStack } from './reactors-avatar-stack';

/** Left-border accent on admin-authored content (announcement / program content). */
const ADMIN_ACCENT_COLOR = '#2A6FB0';

/** Tag pills shown on a feed card before collapsing into a "+N" overflow chip. */
const MAX_VISIBLE_TAGS = 3;

interface PostCardProps {
  post: TPostWithAuthor;
  spaceId: string;
  variant?: 'feed' | 'detail';
  onAfterDelete?: () => void;
  /**
   * Toggles the tag in the feed's tag filter when a post tag chip is clicked.
   * Receives the tag's filter value (`refId ?? id`), matching the trending-tags
   * widget's convention. When omitted, tag chips render as static labels.
   */
  onTagClick?: (value: string) => void;
  /** Filter values currently active, so matching post tags render as selected. */
  selectedTagValues?: string[];
}

// Memoized: the feed renders many cards inside a virtualized list, so a feed
// re-render (search/filter change, data refresh, composer toggle) must not
// re-render every visible card — only cards whose `post` actually changed.
export const PostCard = memo(
  ({ post, spaceId, variant = 'feed', onAfterDelete, onTagClick, selectedTagValues }: PostCardProps) => {
    const navigate = useNavigate();
    const isDetail = variant === 'detail';
    const [showFullBody, setShowFullBody] = useState(false);
    const [isBodyOverflowing, setIsBodyOverflowing] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);
    const [showComments, setShowComments] = useState(false);
    const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
    // Whether the edit composer is open (inline md+, bottom sheet on phones).
    const [isEditing, setIsEditing] = useState(false);

    const theme = useTheme();
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'));
    const { setConfirm } = useConfirmService();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const token = getSession();
    const { data: profile } = useGetAllProfileQuery({ token });
    const { data: comments } = useGetComments(post.id);
    const deletePost = useDeletePost(post.id);
    // Pin/unpin from the list targets any post (incl. others'), so it goes
    // through the admin endpoint rather than the author-scoped update.
    const adminUpdatePost = useUpdateAdminPost(post.id);
    const toggleSavePost = useToggleSavePost();

    const isAuthor = profile?.authUserId === post.author.id;

    // Held for media processing: only the author sees it, it can't be shared, and
    // it shows a processing / failed badge until every attachment settles.
    const isProcessingMedia = post.status === PostStatus.PROCESSING_MEDIA;
    const hasFailedAttachment = (post.attachments ?? []).some(a => a.processingStatus === 'failed');

    // Space managers get the admin post-card actions (pin) and the composer's
    // admin controls when editing. Gated on the space.manage permission for THIS
    // space's app.
    const { data: space } = useGetSpace(spaceId);
    const canManageSpace = userCanManageSpace(profile?.access, space);

    const handleEdit = useCallback(() => {
      setIsEditing(true);
    }, []);

    const handleCopyLink = useCallback(async () => {
      const url = `${window.location.origin}/community-hub/spaces/${spaceId}/posts/${post.id}`;
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        return; // Clipboard unavailable (e.g. insecure context) — skip the feedback.
      }
      enqueueSnackbar('Link copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
    }, [spaceId, post.id]);

    const handleDelete = useCallback(() => {
      setConfirm({
        title: 'Delete',
        open: true,
        confirmBtnLabel: 'Remove',
        cancelBtnLabel: 'Cancel',
        message: 'This will permanently remove your post. Are you sure you want to continue?',
        onConfirm: async () => {
          await deletePost.mutateAsync();
          onAfterDelete?.();
        },
      });
    }, [setConfirm, deletePost, onAfterDelete]);

    // Actions for the "⋯" menu: copy/save for everyone, pin for admins,
    // edit/delete for the author. Rendered as a bottom sheet on phones and a
    // rounded popover on desktop (see PostActionsMenu).
    const menuItems = useMemo<PostActionItem[]>(() => {
      const items: PostActionItem[] = [
        // No shareable link until the post is published — the URL 404s for anyone
        // but the author while media is still processing.
        ...(isProcessingMedia
          ? []
          : [{ id: 'copy-link', label: 'Copy link', icon: <RiLinkM size={18} />, onClick: handleCopyLink }]),
        {
          id: 'save',
          label: post.isSaved ? 'Remove from saved' : 'Save post',
          icon: post.isSaved ? <RiBookmarkFill size={18} /> : <RiBookmarkLine size={18} />,
          onClick: () => toggleSavePost.mutate({ postId: post.id, save: !post.isSaved }),
        },
        ...(canManageSpace
          ? [
              {
                id: 'pin',
                label: post.isPinned ? 'Unpin post' : 'Pin post',
                icon: post.isPinned ? <RiPushpinFill size={18} /> : <RiPushpinLine size={18} />,
                onClick: () => adminUpdatePost.mutate({ isPinned: !post.isPinned }),
              },
            ]
          : []),
        ...(isAuthor
          ? [
              { id: 'edit', label: 'Edit', icon: <FaPencil size={16} />, onClick: handleEdit },
              {
                id: 'delete',
                label: 'Delete',
                icon: <FaRegTrashAlt size={16} />,
                onClick: handleDelete,
                danger: true,
              },
            ]
          : []),
      ];

      // Alphabetical by label, but keep destructive actions (delete) pinned to the
      // bottom so they never sort up next to the safe actions.
      return items.sort((a, b) => Number(!!a.danger) - Number(!!b.danger) || a.label.localeCompare(b.label));
    }, [
      post,
      isAuthor,
      isProcessingMedia,
      canManageSpace,
      adminUpdatePost,
      handleCopyLink,
      toggleSavePost,
      handleEdit,
      handleDelete,
    ]);

    const handleMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
      setMenuAnchor(event.currentTarget);
    }, []);

    const handleBodyClick = (event: React.MouseEvent<HTMLElement>) => {
      if (isDetail) {
        return;
      }
      if ((event.target as HTMLElement).closest('a')) {
        return;
      }
      navigate({
        to: '/community-hub/spaces/$id/posts/$postId',
        params: { id: spaceId, postId: post.id },
      });
    };

    const handleBodyKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
      if (isDetail) {
        return;
      }
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
      navigate({
        to: '/community-hub/spaces/$id/posts/$postId',
        params: { id: spaceId, postId: post.id },
      });
    };

    // Parse Tiptap JSON body and resolve mention display names
    const bodyDoc = useMemo<TiptapDoc | null>(() => {
      try {
        return JSON.parse(post.body);
      } catch {
        return null;
      }
    }, [post.body]);
    const resolvedDoc = useMentionResolver(bodyDoc, fetchMentionUsersByIds);

    const [resolvedBodyHtml, setResolvedBodyHtml] = useState<string>('');

    useEffect(() => {
      if (resolvedDoc) {
        setResolvedBodyHtml(DOMPurify.sanitize(tiptapDocToHtml(resolvedDoc)));
        return;
      }
      const rawHtml = post.bodyHtml ?? post.body;
      const uuidRegex = /@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
      const uuids = [...new Set([...rawHtml.matchAll(uuidRegex)].map(m => m[1]))];
      if (uuids.length === 0) {
        setResolvedBodyHtml(DOMPurify.sanitize(rawHtml));
        return;
      }
      fetchMentionUsersByIds(uuids).then(users => {
        let resolved = rawHtml;
        for (const user of users) {
          resolved = resolved.replaceAll(`@${user.id}`, `<span class="mention">@${user.displayName}</span>`);
        }
        setResolvedBodyHtml(DOMPurify.sanitize(resolved));
      });
    }, [resolvedDoc, post.bodyHtml, post.body]);

    const bodyHtml = resolvedBodyHtml || DOMPurify.sanitize(post.bodyHtml ?? post.body);

    // Detect whether the clamped body actually overflows, so the See more/less
    // toggle only appears when there is hidden content. Measured while clamped;
    // once expanded we keep the last value so "See less" stays available, and a
    // ResizeObserver re-measures on width changes (e.g. responsive layout).
    useEffect(() => {
      if (isDetail || isEditing) {
        return;
      }
      const element = bodyRef.current;
      if (!element) {
        return;
      }
      const measure = () => {
        if (!showFullBody) {
          setIsBodyOverflowing(element.scrollHeight > element.clientHeight + 1);
        }
      };
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }, [bodyHtml, showFullBody, isDetail, isEditing]);

    const timeAgo = formatDistanceToNow(new Date(post.createdAt), { addSuffix: false });
    const postTypeDef = POST_TYPE_DEFINITIONS.find(d => d.value === post.postType);
    // The accent border marks official broadcast posts. Key it on Announcement
    // specifically rather than the admin-only set — other admin-only types (e.g.
    // Discussion) are restricted at create/edit but should still read as normal
    // posts in the feed, so the create guard doesn't leak into the card styling.
    const hasAdminAccent = post.postType === POST_TYPE.ANNOUNCEMENT;

    return (
      <Box
        sx={{
          bgcolor: 'white',
          // Pinned posts keep the normal background but get a light primary-tinted
          // border to stand out gently from the rest of the feed.
          border: post.isPinned ? `1px solid ${theme.palette.primary.main}59` : 'none',
          borderLeft: hasAdminAccent ? `4px solid ${ADMIN_ACCENT_COLOR}` : undefined,
          borderRadius: '15px',
          p: { xs: '12px', md: '15px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        {/* Post Header - Author Info */}
        <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Avatar
            src={getMediaUrl(post.author.avatar)}
            alt={post.author.name}
            sx={{ width: 30, height: 30, borderRadius: '22.5px' }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 700,
                fontSize: { xs: 14, md: 14 },
                lineHeight: '19px',
                color: '#333',
              }}
            >
              {post.author.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 400,
                fontSize: { xs: 9, md: 9 },
                lineHeight: '17px',
                color: '#808080',
              }}
            >
              {timeAgo}
            </Typography>
          </Box>
          {postTypeDef && (
            <Chip
              icon={<PostTypeIcon type={postTypeDef.value} size={13} />}
              label={postTypeDef.label}
              size="small"
              sx={{
                height: 22,
                borderRadius: '11px',
                // 0 8px padding on the chip root. The icon-to-text gap is driven by
                // the label's padding-left (not the icon's margin): under
                // million.js the icon element loses its `MuiChip-icon` class, so
                // `& .MuiChip-icon` rules don't apply — but the label keeps its
                // `MuiChip-label` class.
                px: '8px',
                bgcolor: POST_TYPE_CHIP_COLORS[postTypeDef.value].bg,
                color: POST_TYPE_CHIP_COLORS[postTypeDef.value].color,
                fontFamily: 'Figtree, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                '& .MuiChip-label': { pl: '6px', pr: 0 },
              }}
            />
          )}
          {isProcessingMedia && (
            <Chip
              icon={
                hasFailedAttachment ? <RiErrorWarningLine size={12} /> : <CircularProgress size={11} thickness={5} />
              }
              label={hasFailedAttachment ? 'Upload failed' : 'Processing…'}
              size="small"
              sx={{
                height: 22,
                borderRadius: '11px',
                bgcolor: theme =>
                  hasFailedAttachment ? `${theme.palette.error.main}1A` : `${theme.palette.warning.main}1A`,
                color: theme => (hasFailedAttachment ? theme.palette.error.main : theme.palette.warning.dark),
                fontFamily: 'Figtree, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: theme => (hasFailedAttachment ? theme.palette.error.main : theme.palette.warning.dark),
                  ml: '8px',
                  mr: '-4px',
                },
                '& .MuiChip-label': { px: '8px' },
              }}
            />
          )}
          {post.isPinned && (
            <Chip
              icon={<RiPushpinFill size={12} />}
              label="Pinned"
              size="small"
              sx={{
                height: 22,
                borderRadius: '11px',
                bgcolor: theme => `${theme.palette.primary.main}1A`,
                color: theme => theme.palette.primary.main,
                fontFamily: 'Figtree, sans-serif',
                fontSize: 12,
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: theme => theme.palette.primary.main,
                  ml: '8px',
                  mr: '-4px',
                },
                '& .MuiChip-label': { px: '8px' },
              }}
            />
          )}
          {/* Hidden while editing inline (md+) — the composer owns its own close. */}
          {!(isEditing && !isBelowMd) && (
            <IconButton size="small" sx={{ p: 0 }} onClick={handleMenuClick}>
              <MoreHorizIcon sx={{ fontSize: 18, color: '#333' }} />
            </IconButton>
          )}
          <PostActionsMenu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            items={menuItems}
          />
        </Box>

        {/* Post Content */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          {/* Body — hidden while editing inline (md+); the composer takes its place. */}
          {!(isEditing && !isBelowMd) && (
            <>
              {post.title && (
                <Typography
                  onClick={handleBodyClick}
                  sx={{
                    fontFamily: 'Figtree, sans-serif',
                    fontWeight: 700,
                    fontSize: isDetail ? 20 : 16,
                    lineHeight: 1.3,
                    color: '#1a1a1a',
                    mb: '2px',
                    wordBreak: 'break-word',
                    cursor: isDetail ? 'default' : 'pointer',
                  }}
                >
                  {post.title}
                </Typography>
              )}
              {bodyHtml && (
                <Box
                  ref={bodyRef}
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                  onClick={handleBodyClick}
                  onKeyDown={handleBodyKeyDown}
                  role={isDetail ? undefined : 'button'}
                  tabIndex={isDetail ? undefined : 0}
                  aria-label={isDetail ? undefined : 'Open post'}
                  sx={{
                    ...richHtmlSx,
                    wordBreak: 'break-word',
                    fontFamily: 'Figtree, sans-serif',
                    fontWeight: 400,
                    fontSize: 14,
                    lineHeight: '19px',
                    color: '#333',
                    cursor: isDetail ? 'default' : 'pointer',
                    ...(!isDetail &&
                      !showFullBody && {
                        display: '-webkit-box',
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }),
                    '& span.mention': {
                      color: theme => theme.palette.primary.main,
                      fontWeight: 600,
                      backgroundColor: theme => `${theme.palette.primary.main}22`,
                      borderRadius: '4px',
                      px: '4px',
                      py: '2px',
                    },
                  }}
                />
              )}
            </>
          )}

          {/* Edit composer — inline (md+) in place of the body, or a bottom sheet
            on phones. The same form used to create a post. post-card's `spaceId`
            is already the top-level container id, so it seeds both roles. */}
          <PostComposer
            post={post}
            spaceId={spaceId}
            containerSpaceId={spaceId}
            canManageSpace={canManageSpace}
            open={isEditing}
            onOpenChange={setIsEditing}
            sheetTitle="Edit post"
            sheetSubtitle="Changes are visible once you save"
          />
          {!isEditing && !isDetail && isBodyOverflowing && (
            <Typography
              onClick={event => {
                event.stopPropagation();
                setShowFullBody(prev => !prev);
              }}
              sx={{
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 700,
                fontSize: 12,
                lineHeight: '17px',
                color: theme => theme.palette.primary.main,
                cursor: 'pointer',
              }}
            >
              {showFullBody ? 'See less' : 'See more'}
            </Typography>
          )}

          {/* Post Attachments (images, videos, files) */}
          {!isEditing && post.attachments && post.attachments.length > 0 && (
            <PostAttachmentList attachments={post.attachments} maxHeight={isDetail ? 400 : 300} />
          )}

          {/* Poll */}
          {!isEditing && post.poll && <PollCard poll={post.poll} postId={post.id} />}

          {/* Post Tags — feed cards cap at MAX_VISIBLE_TAGS with a "+N" overflow chip */}
          {!isEditing && post.tags && post.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap', mt: '6px' }}>
              {(isDetail ? post.tags : post.tags.slice(0, MAX_VISIBLE_TAGS)).map(tag => {
                // Filter value follows the trending-tags convention: source entity id
                // for subspace/lesson tags, else the tag's own id (question tags).
                const filterValue = tag.refId ?? tag.id;
                const isSelected = selectedTagValues?.includes(filterValue) ?? false;
                return (
                  <Chip
                    key={tag.id}
                    label={`#${tag.label}`}
                    size="small"
                    onClick={
                      onTagClick
                        ? event => {
                            // Don't let the click bubble into a card-level navigation.
                            event.stopPropagation();
                            onTagClick(filterValue);
                          }
                        : undefined
                    }
                    sx={{
                      height: 18,
                      borderRadius: '5px',
                      // Selected tags echo the feed filter's active colour; the rest
                      // keep a soft tinted background like the body, not the red.
                      bgcolor: theme =>
                        isSelected ? theme.palette.secondary.main : `${theme.palette.secondary.main}29`,
                      color: theme => (isSelected ? theme.palette.common.white : theme.palette.text.primary),
                      fontFamily: 'Figtree, sans-serif',
                      // Metadata-sized — clearly smaller than the post body text (14px).
                      fontSize: 11,
                      fontWeight: 500,
                      ...(onTagClick && {
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: theme =>
                            isSelected ? theme.palette.secondary.dark : `${theme.palette.secondary.main}47`,
                        },
                      }),
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                );
              })}
              {!isDetail && post.tags.length > MAX_VISIBLE_TAGS && (
                <Tooltip
                  title={post.tags
                    .slice(MAX_VISIBLE_TAGS)
                    .map(tag => `#${tag.label}`)
                    .join(', ')}
                >
                  <Chip
                    label={`+${post.tags.length - MAX_VISIBLE_TAGS}`}
                    size="small"
                    sx={{
                      height: 18,
                      borderRadius: '5px',
                      bgcolor: theme => `${theme.palette.secondary.main}29`,
                      color: theme => theme.palette.text.primary,
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 11,
                      fontWeight: 500,
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          )}

          {/* Post Actions */}
          <Box sx={{ display: 'flex', gap: { xs: '10px', md: '15px' }, alignItems: 'center', mt: '5px' }}>
            <Box
              sx={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setShowComments(!showComments)}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: '#333' }} />
              <Typography
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: 9, md: 9 },
                  lineHeight: '17px',
                  color: '#333',
                }}
              >
                Comment {post.commentCount}
              </Typography>
            </Box>

            <ReactionPicker
              targetType="post"
              targetId={post.id}
              currentReaction={post.userReaction}
              likeCount={post.likeCount}
            />

            <ReactorsAvatarStack targetType="post" targetId={post.id} likeCount={post.likeCount} />

            {/* Copy link moved into the card's context menu; save keeps a quick
              icon-only toggle so the row stays compact on small screens. */}
            <Tooltip title={post.isSaved ? 'Remove from saved posts' : 'Save post'}>
              <IconButton
                size="small"
                aria-label={post.isSaved ? 'Remove from saved posts' : 'Save post'}
                onClick={() => toggleSavePost.mutate({ postId: post.id, save: !post.isSaved })}
                sx={{ ml: 'auto', p: '4px' }}
              >
                {post.isSaved ? (
                  <RiBookmarkFill size={15} color={theme.palette.primary.main} />
                ) : (
                  <RiBookmarkLine size={15} color="#333" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Comments Section */}
        {(isDetail || showComments) && comments && comments.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {comments.map((comment: TCommentWithAuthor) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                spaceId={spaceId}
                postId={post.id}
                onReplyClick={() => setReplyToCommentId(comment.id)}
              />
            ))}
          </Box>
        )}

        {/* Comment Input */}
        {(isDetail || showComments || replyToCommentId) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {replyToCommentId && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: 12,
                    color: '#808080',
                  }}
                >
                  Replying to {comments?.find(c => c.id === replyToCommentId)?.author.name || 'comment'}
                </Typography>
                <Typography
                  onClick={() => setReplyToCommentId(null)}
                  sx={{
                    fontFamily: 'Figtree, sans-serif',
                    fontWeight: 700,
                    fontSize: 12,
                    color: '#333',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </Typography>
              </Box>
            )}
            <CommentInput
              spaceId={spaceId}
              postId={post.id}
              parentId={replyToCommentId || undefined}
              onClose={() => setReplyToCommentId(null)}
            />
          </Box>
        )}
      </Box>
    );
  },
);

PostCard.displayName = 'PostCard';
